import { createStore } from './createStore';

const initialState = {
  /** @type {string|null} */
  runId: null,
  /** @type {'idle' | 'running' | 'completed' | 'error' | 'aborted'} */
  runStatus: 'idle',
  /** @type {string|null} - ISO timestamp for the run start */
  runStartedAt: null,
  /** @type {{ prompt?: string|null, modelTier?: string|null, attachmentCount?: number|null }|null} */
  runMeta: null,
  /** @type {Array<Object>} - Ordered by seq */
  events: [],
  /** @type {Array<Object>|null} - Parsed from latest todo_update event */
  todos: null,
  /** @type {string|null} - Event ID whose result is shown in the canvas */
  selectedEventId: null,
  /** @type {string|null} - For batch results: the specific asset URL that was clicked,
   * so the result view can open on that item instead of the first one. */
  selectedEventUrl: null,
  /** @type {boolean} - True once ProjectPage has checked whether a composer run exists */
  hydrationChecked: false,
  /** @type {Object|null} - Parsed cost_estimate result_data { ai_video_seconds, remotion_seconds, model_tier } */
  costEstimate: null,
  /** @type {boolean} - True when the current cost estimate banner has been dismissed */
  costEstimateDismissed: false,
  /** @type {null|'inline-edit'} - Origin of the active run. 'inline-edit' means it was started
   * by the in-canvas composer; the sidebar should not auto-take over the right panel. */
  runSource: null,
};

/** Derive todo and cost estimate patches from a fully merged event. */
function deriveEventExtras(event, state) {
  const extra = {};
  if (event.event_type === 'todo_update' && event.content) {
    try {
      extra.todos = JSON.parse(event.content);
    } catch { /* ignore parse errors */ }
  }
  if (event.event_type === 'cost_estimate' && event.result_data) {
    extra.costEstimate = event.result_data;
    extra.costEstimateDismissed = false;
  }
  return extra;
}

export const useAgentWorklogStore = createStore('AgentWorklogStore', (set, get) => ({
  ...initialState,

  setRunId: (runId) => set({ runId }),

  setRunStatus: (runStatus) => set({ runStatus }),

  setRunStartedAt: (runStartedAt) => set({ runStartedAt }),

  setRunMeta: (runMeta) => set({ runMeta }),

  setRunSource: (runSource) => set({ runSource }),

  setHydrationChecked: (hydrationChecked) => set({ hydrationChecked }),

  /**
   * Insert or update an event. On INSERT the event is appended in seq order.
   * On UPDATE (same id) the existing entry is patched in place.
   */
  upsertEvent: (event) =>
    set((state) => {
      const idx = state.events.findIndex((e) => e.id === event.id);
      if (idx >= 0) {
        const merged = { ...state.events[idx], ...event };
        const updated = [...state.events];
        updated[idx] = merged;
        return { events: updated, ...deriveEventExtras(merged, state) };
      }
      const events = [...state.events, event].sort(
        (a, b) => (a.seq ?? 0) - (b.seq ?? 0),
      );

      return { events, ...deriveEventExtras(event, state) };
    }),

  setTodos: (todos) => set({ todos }),

  dismissCostEstimate: () => set({ costEstimateDismissed: true }),

  setSelectedEventId: (selectedEventId, selectedEventUrl = null) =>
    set({ selectedEventId, selectedEventUrl }),

  acknowledgeAbort: () => set({
    runStatus: 'idle',
    selectedEventId: null,
    selectedEventUrl: null,
    costEstimate: null,
    costEstimateDismissed: false,
    runSource: null,
    runStartedAt: null,
    runMeta: null,
  }),

  /**
   * Bulk load events on page refresh. Also extracts the latest todos.
   */
  hydrateFromEvents: (events) => {
    const sorted = [...events].sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
    let todos = null;
    let costEstimate = null;
    for (let i = sorted.length - 1; i >= 0; i--) {
      if (!todos && sorted[i].event_type === 'todo_update' && sorted[i].content) {
        try {
          todos = JSON.parse(sorted[i].content);
        } catch { /* ignore */ }
      }
      if (!costEstimate && sorted[i].event_type === 'cost_estimate' && sorted[i].result_data) {
        costEstimate = sorted[i].result_data;
      }
      if (todos && costEstimate) break;
    }
    const patch = { events: sorted, todos };
    if (costEstimate) {
      patch.costEstimate = costEstimate;
    }
    set(patch);
  },

  resetWorklog: () => set(() => initialState),
}));
